// Class colours picked from a palette rather than typed as hex (customer feedback, Oliver: "I don't know the hex code of
// a colour"). Pure helpers behind dialogs/ClassColorDialog.tsx, kept free of React so they can be tested.

export interface Swatch { hex: string; name: string }

/**
 * The palette. The first eight are the colours an import gives a class it creates (backend/app/models/models.py
 * IMPORT_CLASS_COLOURS), in the same order, so a class added by hand and one added by an import look alike. The rest
 * stay apart from each other and from the canvas's own colours: orange #FF8C00 for unreviewed machine annotations,
 * light blue #4FC3F7 and green #81C784 for shapes with no class.
 */
export const SWATCHES: Swatch[] = [
  { hex: "#FF6B35", name: "Orange" }, { hex: "#4ECDC4", name: "Turquoise" }, { hex: "#FFD23F", name: "Yellow" },
  { hex: "#7B61FF", name: "Violet" }, { hex: "#2EC4B6", name: "Teal" }, { hex: "#E71D36", name: "Red" },
  { hex: "#8AC926", name: "Lime" }, { hex: "#FF9F1C", name: "Amber" }, { hex: "#1982C4", name: "Blue" },
  { hex: "#FF4081", name: "Pink" }, { hex: "#00C853", name: "Green" }, { hex: "#9C27B0", name: "Purple" },
  { hex: "#00B8D9", name: "Cyan" }, { hex: "#F15BB5", name: "Magenta" }, { hex: "#3D5AFE", name: "Indigo" },
  { hex: "#C0CA33", name: "Olive" }, { hex: "#8D6E63", name: "Brown" }, { hex: "#607D8B", name: "Slate" },
  { hex: "#FFFFFF", name: "White" }, { hex: "#9E9E9E", name: "Grey" },
];
/** Columns of the swatch grid; the arrow keys move by one, or by a row. */
export const SWATCH_COLUMNS = 10;

/** "#ff6b35", "FF6B35" and "#f63" all read as "#FF6B35"-style codes; anything else is null. */
export function normalizeHex(text: string): string | null {
  const t = text.trim().replace(/^#/, "");
  if (/^[0-9a-f]{6}$/i.test(t)) return `#${t.toUpperCase()}`;
  if (/^[0-9a-f]{3}$/i.test(t)) return `#${[...t].map((c) => c + c).join("").toUpperCase()}`;
  return null;
}

/** The colour's name in the palette ("Orange"), or its code. */
export const colorName = (hex: string) => SWATCHES.find((s) => s.hex === hex.toUpperCase())?.name ?? hex.toUpperCase();

/** A new class's colour: the first in the palette no class has yet, so classes look different; the first when all are taken. */
export function nextColor(taken: string[]): string {
  const used = new Set(taken.map((h) => h.toUpperCase()));
  return (SWATCHES.find((s) => !used.has(s.hex)) ?? SWATCHES[0]).hex;
}

/** Black or white, whichever reads better on `hex`. */
export function textOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#fff";
  const v = parseInt(m[1], 16);
  const lin = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const lum = 0.2126 * lin(v >> 16) + 0.7152 * lin((v >> 8) & 255) + 0.0722 * lin(v & 255);
  return lum > 0.4 ? "#000" : "#fff";
}

/** Arrow keys in the grid: the index to move to, kept inside the palette; null for any other key. */
export function moveInGrid(index: number, key: string, count = SWATCHES.length, cols = SWATCH_COLUMNS): number | null {
  const step: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols, Home: -count, End: count };
  if (!(key in step)) return null;
  return Math.min(count - 1, Math.max(0, index + step[key]));
}
